/**
 * Motion: a camera and a drawing hand.
 *
 * DESIGN.md names three signatures and nothing else moves:
 *
 *   1. Settling — a product shot arrives already in place and settles the last
 *      few pixels, with its pool of light coming up to meet it. A camera
 *      finding focus, not an object flying in.
 *   2. Drawing — an annotation is drawn: the dot lands, the leader grows out
 *      of it toward the margin, and only then does the text appear at the far
 *      end. In order down the stage, so the eye is led around the object.
 *   3. Breathing — a lit lamp dome's glow varies a few percent, in CSS.
 *
 * Everything here is off under `prefers-reduced-motion`, where the page is the
 * same studio photographed rather than filmed: shots placed, leaders drawn
 * full, lamps lit. That is a designed state, not an absence — the CSS renders
 * it, so returning early here leaves the page correct.
 */

import type { SkinMotion } from '@remidus/web-core'

const reduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const motion: SkinMotion = {
  reducedMotion: 'respect',

  init: (root) => {
    const teardown: (() => void)[] = []

    if (reduced()) {
      root.classList.add('skin-motion--off')
      return () => teardown.forEach((fn) => fn())
    }

    void (async () => {
      const [{ default: Lenis }, { gsap }, { ScrollTrigger }] = await Promise.all([
        import('lenis'),
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ])
      gsap.registerPlugin(ScrollTrigger)

      const lenis = new Lenis({
        duration: 1.05,
        easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
      })
      let frame = 0
      const raf = (time: number): void => {
        lenis.raf(time)
        frame = requestAnimationFrame(raf)
      }
      frame = requestAnimationFrame(raf)
      lenis.on('scroll', ScrollTrigger.update)
      teardown.push(() => {
        cancelAnimationFrame(frame)
        lenis.destroy()
      })

      // ── 1. Settling ─────────────────────────────────────────────────────
      // The shot and its pool are separate elements so they can settle at
      // different rates: the object lands first and the light catches up,
      // which is what makes it read as a lamp rather than as a drop shadow.
      for (const stage of root.querySelectorAll<HTMLElement>('.skin-plinth')) {
        const shot = stage.querySelector<HTMLElement>('.skin-plinth__shot')
        const pool = stage.querySelector<HTMLElement>('.skin-plinth__pool')
        const settle = gsap.timeline({
          scrollTrigger: { trigger: stage, start: 'top 88%', once: true },
        })
        if (shot) settle.from(shot, { y: 22, opacity: 0, duration: 0.72, ease: 'power3.out' }, 0)
        if (pool) settle.from(pool, { opacity: 0, scaleX: 0.7, duration: 0.9, ease: 'power2.out' }, 0.12)
        teardown.push(() => settle.kill())
      }

      // Parallax: the object drifts against the page as it passes, which is
      // the only continuous scroll effect left. Small on purpose — DESIGN.md
      // puts MOTION at 3 and a showcase that performs undercuts its subject.
      for (const shot of root.querySelectorAll<HTMLElement>('.skin-plinth--anatomy .skin-plinth__shot')) {
        const drift = gsap.to(shot, {
          yPercent: -6,
          ease: 'none',
          scrollTrigger: { trigger: shot, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
        })
        teardown.push(() => drift.kill())
      }

      // ── 2. Drawing ──────────────────────────────────────────────────────
      // Re-run whenever the block re-measures: the leaders are rebuilt from
      // scratch on resize, and the new paths would otherwise sit at whatever
      // dash offset the old ones were left at.
      for (const field of root.querySelectorAll<HTMLElement>('.skin-anatomy')) {
        let played = false
        const drawAll = (): void => {
          const dots = field.querySelectorAll<HTMLElement>('.skin-anatomy__dot')
          const leaders = field.querySelectorAll<SVGPathElement>('.skin-anatomy__leader')
          const notes = field.querySelectorAll<HTMLElement>('.skin-anatomy__note')
          const line = gsap.timeline()
          for (let i = 0; i < notes.length; i += 1) {
            const at = i * 0.16
            if (dots[i]) line.fromTo(dots[i]!, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.24, ease: 'back.out(2.2)' }, at)
            if (leaders[i]) {
              const length = Number(leaders[i]!.style.getPropertyValue('--leader-length')) || 300
              line.fromTo(
                leaders[i]!,
                { strokeDashoffset: length },
                { strokeDashoffset: 0, duration: 0.32, ease: 'power2.inOut' },
                at + 0.14,
              )
            }
            if (notes[i]) line.fromTo(notes[i]!, { opacity: 0, x: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' }, at + 0.34)
          }
          teardown.push(() => line.kill())
        }

        const start = ScrollTrigger.create({
          trigger: field,
          start: 'top 76%',
          once: true,
          onEnter: () => {
            played = true
            drawAll()
          },
        })
        teardown.push(() => start.kill())
        // A redraw after the animation has played must not leave the new
        // paths hidden: they are created at full dash offset.
        const redraw = (): void => {
          if (played) drawAll()
        }
        field.addEventListener('skin:leaders', redraw)
        teardown.push(() => field.removeEventListener('skin:leaders', redraw))
      }

      // Everything else arrives the same quiet way, once. Stages that own a
      // plinth or a set of leaders have their own choreography above.
      const rest = root.querySelectorAll<HTMLElement>(
        '.skin-stage:not(:has(.skin-plinth)):not(:has(.skin-anatomy))',
      )
      for (const section of rest) {
        const reveal = gsap.from(section, {
          opacity: 0,
          y: 18,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: { trigger: section, start: 'top 86%', once: true },
        })
        teardown.push(() => reveal.kill())
      }

      teardown.push(() => ScrollTrigger.getAll().forEach((t) => t.kill()))
    })()

    return () => teardown.forEach((fn) => fn())
  },
}

export default motion
